import React, { useState } from "react";
import { CheckCircle, Download, Eye, FileText, Save, X } from "lucide-react";
import { apiFetch } from "../../../../context/AuthContext";
import { API_URL } from "../../config";
import { DOCUMENT_CHECKLIST_FIELDS } from "../../constants/documentChecklistFields";

// ===== DOCUMENTS MODAL COMPONENT =====
//
// Adapted from the admin Employee Management page's DocumentsModal so both
// pages show the same thing: the full 14-item checklist (previously this
// modal hardcoded a 7-item subset) AND the employee's actual uploaded
// documents with preview/download — this page's version used to only show
// the checklist, with no way to see or open a real uploaded file at all.
const DocumentsModal = ({
  styles,
  text,
  language,
  screenSize,
  fonts,
  spacing,
  isDarkMode,
  selectedEmployeeDocuments,
  loadingDocuments = false,
  documentChecklist,
  hasUnsavedChanges,
  savingDocument,
  onCheckboxChange,
  onSave,
  onBackdropClose,
  onClose,
}) => {
  const [actionError, setActionError] = useState("");

  if (!selectedEmployeeDocuments) return null;

  const handleModalClick = (e) => {
    if (e.target === e.currentTarget) onBackdropClose();
  };

  const handlePreview = async (documentId) => {
    try {
      setActionError("");
      const response = await apiFetch(`${API_URL}/api/documents/preview/${documentId}`, {
        method: "GET",
      });
      if (!response.ok) throw new Error("Failed to preview document");
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      window.open(url, "_blank");
      setTimeout(() => window.URL.revokeObjectURL(url), 100);
    } catch (err) {
      console.error("Preview error:", err);
      setActionError(language === "ar" ? "فشل في عرض المستند" : "Failed to preview document");
    }
  };

  const handleDownload = async (documentId) => {
    try {
      setActionError("");
      const response = await apiFetch(`${API_URL}/api/documents/download/${documentId}`, {
        method: "GET",
      });
      if (!response.ok) throw new Error("Failed to download document");

      const blob = await response.blob();
      const arrayBuffer = await blob.arrayBuffer();
      const bytes = new Uint8Array(arrayBuffer);

      let filename = null;
      const contentDisposition = response.headers.get("Content-Disposition");
      if (contentDisposition) {
        const match = contentDisposition.match(/filename\*?=(?:UTF-8''|")?([^;"\n]+)/);
        if (match && match[1]) filename = decodeURIComponent(match[1].replace(/"/g, ""));
      }

      const mimeToExt = {
        "application/pdf": "pdf",
        "application/msword": "doc",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "docx",
        "application/vnd.ms-excel": "xls",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "xlsx",
        "image/png": "png",
        "image/jpeg": "jpg",
        "image/webp": "webp",
        "text/plain": "txt",
        "application/zip": "zip",
      };
      let extension = mimeToExt[blob.type];
      if (!extension) {
        const hex = [...bytes.slice(0, 8)].map((b) => b.toString(16).padStart(2, "0")).join("");
        if (hex.startsWith("25504446")) extension = "pdf";
        else if (hex.startsWith("504b0304")) extension = "zip";
        else if (hex.startsWith("ffd8ff")) extension = "jpg";
        else if (hex.startsWith("89504e47")) extension = "png";
        else extension = "bin";
      }

      if (!filename) filename = `document.${extension}`;
      else if (!filename.includes(".")) filename = `${filename}.${extension}`;

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => window.URL.revokeObjectURL(url), 200);
    } catch (err) {
      console.error("Download error:", err);
      setActionError(language === "ar" ? "فشل في تحميل المستند" : "Failed to download document");
    }
  };

  return (
    <div style={styles.modal} onClick={handleModalClick}>
      <div style={styles.viewModalContent} onClick={(e) => e.stopPropagation()}>
        {/* Modal Header */}
        <div style={styles.modalHeader}>
          <h2 style={styles.modalTitle}>
            <FileText size={screenSize === 'xs' ? 18 : 20} />
            {text[language].documentChecklist} - {
              selectedEmployeeDocuments.name
                ?.split(' ')
                .slice(0, 4)
                .join(' ')
            }

            {hasUnsavedChanges && (
              <span style={{
                display: 'inline-flex',
                alignItems: 'center',
                marginLeft: '1rem',
                fontSize: fonts.small,
                color: '#f59e0b',
                fontWeight: '500'
              }}>
                ● {language === 'ar' ? 'تغييرات غير محفوظة' : 'Unsaved Changes'}
              </span>
            )}
          </h2>
          <button style={styles.closeBtn} onClick={onClose}>
            <X size={screenSize === "xs" ? 20 : 24} />
          </button>
        </div>

        {actionError && (
          <div
            style={{
              marginBottom: spacing.margin,
              padding: "10px 14px",
              borderRadius: 10,
              background: isDarkMode ? "rgba(239,68,68,0.12)" : "rgba(239,68,68,0.08)",
              color: "#ef4444",
              fontSize: fonts.small,
              fontWeight: 600,
            }}
          >
            {actionError}
          </div>
        )}

        <div style={styles.viewModalBody}>
          {/* Uploaded documents (real files) */}
          {loadingDocuments && (
            <div style={{
              textAlign: 'center',
              padding: '3rem',
              color: isDarkMode ? 'rgba(255, 255, 255, 0.6)' : 'rgba(26, 26, 26, 0.6)',
            }}>
              {text[language].loading}
            </div>
          )}

          {!loadingDocuments && Array.isArray(selectedEmployeeDocuments.documents) && (
            <div>
              {selectedEmployeeDocuments.documents.length === 0 ? (
                <div style={{
                  textAlign: 'center',
                  padding: '2rem',
                  color: isDarkMode ? 'rgba(255, 255, 255, 0.6)' : 'rgba(26, 26, 26, 0.6)',
                }}>
                  {language === "ar" ? "لا توجد مستندات" : "No documents found"}
                </div>
              ) : (
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: screenSize === 'xs' ? '1fr' : 'repeat(auto-fill, minmax(220px, 1fr))',
                  gap: spacing.gap,
                }}>
                  {selectedEmployeeDocuments.documents.map((doc) => {
                    const fileName = doc.filePath ? doc.filePath.split('/').pop() : 'unknown';
                    const fileExtension = fileName.includes('.') ? fileName.split('.').pop().toUpperCase() : 'FILE';

                    return (
                      <div
                        key={doc.id}
                        style={{
                          background: isDarkMode ? "rgba(255, 255, 255, 0.05)" : "rgba(0, 0, 0, 0.03)",
                          border: isDarkMode ? "1px solid rgba(255, 255, 255, 0.1)" : "1px solid rgba(0, 0, 0, 0.1)",
                          borderRadius: screenSize === "xs" ? "0.75rem" : "1rem",
                          padding: spacing.padding,
                        }}
                      >
                        <div style={{
                          width: '48px',
                          height: '48px',
                          background: 'rgba(139, 92, 246, 0.1)',
                          borderRadius: '12px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          margin: '0 auto 0.75rem',
                        }}>
                          <FileText size={26} color="#8b5cf6" />
                        </div>

                        <div style={{
                          fontSize: fonts.body,
                          fontWeight: '600',
                          color: isDarkMode ? '#fff' : '#1a1a1a',
                          marginBottom: '0.25rem',
                          textAlign: 'center',
                        }}>
                          {doc.type}
                        </div>

                        <div style={{
                          fontSize: fonts.small,
                          color: isDarkMode ? 'rgba(255, 255, 255, 0.6)' : 'rgba(26, 26, 26, 0.6)',
                          marginBottom: '0.75rem',
                          textAlign: 'center',
                        }}>
                          {fileExtension}
                        </div>

                        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
                          <button
                            style={{ ...styles.actionBtn, ...styles.viewBtn, flex: 1 }}
                            onClick={() => handlePreview(doc.id)}
                            title={language === "ar" ? "عرض" : "View"}
                          >
                            <Eye size={14} />
                          </button>
                          <button
                            style={{ ...styles.actionBtn, ...styles.documentsBtn, flex: 1 }}
                            onClick={() => handleDownload(doc.id)}
                            title={language === "ar" ? "تحميل" : "Download"}
                          >
                            <Download size={14} />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Document Checklist */}
          {!loadingDocuments && (
            <div>
              <div style={{
                color: isDarkMode ? '#fff' : '#1a1a1a',
                fontSize: fonts.subtitle,
                fontWeight: '600',
                marginBottom: spacing.gap,
              }}>
                {text[language].documentChecklist}
              </div>

              <div style={{
                display: 'grid',
                gridTemplateColumns: screenSize === 'xs' || screenSize === 'sm' ? '1fr' : 'repeat(2, 1fr)',
                gap: spacing.margin,
              }}>
                {DOCUMENT_CHECKLIST_FIELDS.map(({ name: field, label }) => (
                  <label
                    key={field}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.75rem',
                      cursor: 'pointer',
                      padding: '1rem',
                      borderRadius: '8px',
                      backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
                      border: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={Boolean(documentChecklist[field])}
                      onChange={(e) => onCheckboxChange(field, e.target.checked)}
                      style={{
                        width: '20px',
                        height: '20px',
                        cursor: 'pointer',
                        accentColor: '#10b981'
                      }}
                    />
                    <span style={{
                      fontSize: fonts.body,
                      color: isDarkMode ? '#fff' : '#1f2937',
                      fontWeight: '500',
                      flex: 1
                    }}>
                      {label[language]}
                    </span>
                    {documentChecklist[field] && (
                      <CheckCircle size={18} style={{ color: '#10b981' }} />
                    )}
                  </label>
                ))}
              </div>

              {/* Save Button */}
              <div style={{
                marginTop: spacing.margin,
                display: 'flex',
                justifyContent: 'flex-end',
                gap: spacing.margin
              }}>
                <button
                  onClick={onSave}
                  disabled={!hasUnsavedChanges || savingDocument}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: screenSize === 'xs' ? '0.75rem 1.5rem' : '1rem 2rem',
                    fontSize: fonts.body,
                    fontWeight: '600',
                    color: '#fff',
                    backgroundColor: hasUnsavedChanges ? '#10b981' : '#94a3b8',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: hasUnsavedChanges && !savingDocument ? 'pointer' : 'not-allowed',
                    transition: 'all 0.2s ease',
                    opacity: savingDocument ? 0.7 : 1,
                  }}
                  onMouseEnter={(e) => {
                    if (hasUnsavedChanges && !savingDocument) {
                      e.target.style.backgroundColor = '#059669';
                      e.target.style.transform = 'translateY(-1px)';
                      e.target.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (hasUnsavedChanges && !savingDocument) {
                      e.target.style.backgroundColor = '#10b981';
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = 'none';
                    }
                  }}
                >
                  {savingDocument ? (
                    <>
                      <Save size={18} style={{ animation: 'pulse 1.5s ease-in-out infinite' }} />
                      {language === 'ar' ? 'جاري الحفظ...' : 'Saving...'}
                    </>
                  ) : (
                    <>
                      <Save size={18} />
                      {language === 'ar' ? 'حفظ التغييرات' : 'Save Changes'}
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DocumentsModal;
